// external
import styled from 'styled-components';
import { Typography } from '@interstate/components/Typography';

export const AiAltVehicleOptionsSectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.tokens.BaseSpace40};
    background-color: ${({ theme }) => theme.tokens.SemColorSurfaceDefault};
    border: 1px solid ${({ theme }) => theme.tokens.SemColorBorderMuted};
    border-radius: ${({ theme }) => theme.tokens.ComponentCardBorderRadius};
    overflow: hidden;
`;

export const AiAltVehicleOptionsSectionBanner = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
    width: 100%;
    background-color: ${({ theme }) => theme.tokens.SemColorSecondary800};
    padding: 0 ${({ theme }) => theme.tokens.BaseSpace40};
    border: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
`;

export const AiAltVehicleOptionsSectionHeader = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 34px;
    padding: 0 ${({ theme }) => theme.tokens.BaseSpace10};
`;

export const AiAltVehicleOptionsChevron = styled.span<{ isExpanded: boolean }>`
    display: inline-flex;
    align-items: center;
    transition: transform 220ms ease;
    transform: rotate(${({ isExpanded }) => (isExpanded ? '0deg' : '-90deg')});
`;

export const AiAltVehicleOptionsCollapsible = styled.div<{ isExpanded: boolean }>`
    display: grid;
    grid-template-rows: ${({ isExpanded }) => (isExpanded ? '1fr' : '0fr')};
    visibility: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
    overflow: hidden;
    margin-top: ${({ isExpanded, theme }) => (isExpanded ? theme.tokens.BaseSpace20 : '0')};
    transition: grid-template-rows 220ms ease, margin-top 220ms ease, visibility 220ms ease;

    > * {
        min-height: 0;
    }
`;

export const AiAltVehicleOptionsHeaderTitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
`;

export const AiAltVehicleOptionsHeaderTitle = styled(Typography)`
    && {
        color: ${({ theme }) => theme.tokens.SemColorOnSurfaceInverse};
    }
`;

export const AiAltVehicleOptionsSectionSubtitle = styled(Typography)`
    && {
        color: ${({ theme }) => theme.tokens.SemColorOnSurfaceInverse};
    }
`;

export const AiAltVehicleOptionsCardRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
    padding: 0 ${({ theme }) => theme.tokens.BaseSpace40} ${({ theme }) => theme.tokens.BaseSpace40};
`;

export const AiAltVehicleOptionsCardContainer = styled.div<{ isSelected: boolean }>`
    display: flex;
    width: 100%;
    padding: ${({ theme }) => theme.tokens.BaseSpace20};
    background-color: ${({ theme, isSelected }) =>
        isSelected ? theme.tokens.SemColorSurfaceInteractiveSelected : theme.tokens.SemColorSurfaceDefault};
    border: ${({ theme, isSelected }) =>
        isSelected
            ? `2px solid ${theme.tokens.SemColorBorderInteractiveSelected}`
            : `1px solid ${theme.tokens.SemColorBorderDefault}`};
    border-radius: ${({ theme }) => theme.tokens.ComponentCardBorderRadius};
    box-shadow: ${({ theme, isSelected }) => (isSelected ? theme.tokens.BaseShadowSm : 'none')};
    cursor: pointer;

    /* The card is selectable by keyboard as well as pointer, so focus has to be visible — reusing the selected
       border's token rather than the browser default, which is invisible against the selected background. */
    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.tokens.SemColorBorderInteractiveSelected};
        outline-offset: 2px;
    }
`;

export const AiAltVehicleOptionsCardVehicleInfo = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
    width: 100%;
    min-width: 0;
`;

export const AiAltVehicleOptionsCardImage = styled.img`
    width: 124px;
    height: 57.465px;
    object-fit: cover;
    flex-shrink: 0;
`;

export const AiAltVehicleOptionsCardDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
    flex: 1 1 0;
    min-width: 0;
`;

export const AiAltVehicleOptionsCardVehicleDetails = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};
    min-width: 0;
`;

export const AiAltVehicleOptionsCardMetricsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: ${({ theme }) => theme.tokens.BaseSpace05} ${({ theme }) => theme.tokens.BaseSpace40};
    width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.values.xl}px) {
        justify-content: space-between;
    }
`;

export const AiAltVehicleOptionsCardMetric = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.tokens.BaseSpace05};
    flex: 1 1 0;
    min-width: max-content;

    @media (max-width: ${({ theme }) => theme.breakpoints.values.xl}px) {
        flex: 0 1 auto;
        min-width: 59px;
    }
`;

export const AiAltVehicleOptionsCardSummaryMetric = styled(AiAltVehicleOptionsCardMetric)`
    @media (max-width: ${({ theme }) => theme.breakpoints.values.xl}px) {
        flex: 1 1 0;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: ${({ theme }) => theme.tokens.BaseSpace20};
        min-height: 24px;
    }
`;

export const AiAltVehicleOptionsCardAcceptanceMetric = styled(AiAltVehicleOptionsCardSummaryMetric)`
    flex: 0 0 auto;

    @media (max-width: ${({ theme }) => theme.breakpoints.values.xl}px) {
        flex: 1 1 0;
    }
`;

export const AiAltVehicleOptionsCardDivider = styled.hr`
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.values.xl}px) {
        display: block;
        flex: 0 0 100%;
        border: none;
        border-top: 1px solid rgba(0, 0, 0, 0.2);
        margin: ${({ theme }) => theme.tokens.BaseSpace10} 0 0;
    }
`;

export const AiAltVehicleOptionsCardActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.tokens.BaseSpace20};

    > * {
        flex: 0 0 120px;
    }
`;
